import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { useCreateIndividual, useIndividual, useUpdateIndividual } from "@/hooks/useCRM";
import ChipListEditor from "@/components/ChipListEditor";

export default function CRMIndividualEdit() {
  const navigate = useNavigate();
  const { individualId } = useParams<{ individualId: string }>();
  const [searchParams] = useSearchParams();
  // ?company_id=… is honoured on create (pre-fills the parent company).
  // On edit, the individual's company_id is loaded from the row itself.
  const companyIdParam = searchParams.get("company_id");
  const isNew = !individualId;

  const { data: existing, isLoading } = useIndividual(individualId);
  const createMutation = useCreateIndividual();
  // useUpdateIndividual needs a stable id — empty string when creating is
  // never invoked because we only call updateMutation in edit mode.
  const updateMutation = useUpdateIndividual(individualId ?? "");

  const [form, setForm] = useState<Record<string, unknown>>({
    company_id: companyIdParam,
    first_name: "",
    last_name: "",
    job_title: "",
    email: "",
    phone: "",
    mobile: "",
    linkedin_url: "",
    is_primary_contact: false,
    source: "",
    tags: [] as string[],
    notes: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  // Populate the form from the loaded row when editing.
  useEffect(() => {
    if (existing) {
      setForm({
        company_id: existing.company_id,
        first_name: existing.first_name,
        last_name: existing.last_name,
        job_title: existing.job_title ?? "",
        email: existing.email ?? "",
        phone: existing.phone ?? "",
        mobile: existing.mobile ?? "",
        linkedin_url: existing.linkedin_url ?? "",
        is_primary_contact: existing.is_primary_contact,
        source: existing.source ?? "",
        tags: existing.tags ?? [],
        notes: existing.notes ?? "",
      });
    }
  }, [existing]);

  const update = (field: string, value: unknown) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    const data: Record<string, unknown> = { ...form };
    for (const key of Object.keys(data)) {
      if (data[key] === "") data[key] = null;
    }
    if ((data.tags as string[])?.length === 0) data.tags = null;

    const destinationCompanyId = (data.company_id as string | null) ?? companyIdParam ?? null;

    try {
      if (isNew) {
        const created = await createMutation.mutateAsync(data);
        if (created.company_id) {
          navigate(`/crm/companies/${created.company_id}`);
        } else {
          navigate("/crm");
        }
      } else {
        await updateMutation.mutateAsync(data);
        setSnackbar({ open: true, message: "Contact saved", severity: "success" });
        // Brief delay so the user sees the confirmation, then go back.
        if (destinationCompanyId) {
          setTimeout(() => navigate(`/crm/companies/${destinationCompanyId}`), 700);
        }
      }
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: string } }; message?: string }).response?.data
          ?.detail ??
        (e as Error).message ??
        "Failed to save";
      setSnackbar({
        open: true,
        message: typeof msg === "string" ? msg : "Failed to save",
        severity: "error",
      });
    }
  };

  if (!isNew && isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const backTarget = isNew
    ? companyIdParam
      ? `/crm/companies/${companyIdParam}`
      : "/crm"
    : (form.company_id as string | null)
      ? `/crm/companies/${form.company_id}`
      : "/crm";

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(backTarget)}>
          Back
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>
          {isNew ? "New Contact" : "Edit Contact"}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={isPending || !form.first_name || !form.last_name}
        >
          {isPending ? "Saving..." : "Save"}
        </Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Card>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Personal Details</Typography>
            <TextField
              label="First Name"
              value={form.first_name}
              onChange={(e) => update("first_name", e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={form.last_name}
              onChange={(e) => update("last_name", e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Job Title"
              value={form.job_title}
              onChange={(e) => update("job_title", e.target.value)}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_primary_contact as boolean}
                  onChange={(e) => update("is_primary_contact", e.target.checked)}
                />
              }
              label="Primary Contact"
            />
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Card>
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="h6">Contact Details</Typography>
              <TextField
                label="Email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                fullWidth
              />
              <TextField
                label="Phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                fullWidth
              />
              <TextField
                label="Mobile"
                value={form.mobile}
                onChange={(e) => update("mobile", e.target.value)}
                fullWidth
              />
              <TextField
                label="LinkedIn URL"
                value={form.linkedin_url}
                onChange={(e) => update("linkedin_url", e.target.value)}
                fullWidth
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Source"
                value={form.source}
                onChange={(e) => update("source", e.target.value)}
                fullWidth
              />
              <ChipListEditor
                label="Tags"
                values={(form.tags as string[]) ?? []}
                onChange={(v) => update("tags", v)}
              />
              <TextField
                label="Notes"
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
